import { PdsWidget } from '../../pds-widget';
import {GridWidget } from '../../models/grid-widget/grid-widget';
import { pdsUtils } from '../../pds-utils';
import { pdsValidation } from '../../pds-validation';
import { pdsI18n } from '../../pds-i18n';
import { FilterContainer } from '../../models/grid-widget/filter-container';
import { GridWidgetDataField } from '../../models/grid-widget/grid-widget-data-field';
import { ElementConfiguration } from '../../interfaces/element-configuration';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { pdsLogger } from '../../pds-logger';
import { serviceInstances } from '../../services/service-instances';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('gridWidget'), 'pds-grid-widget-filter.ts');

export class PdsGridWidgetFilter extends PdsWidget {
  is = 'pds-grid-widget-filter';
  name: string;
  dataGridWidgetId: string | null;
  gridWidget: GridWidget | null = null;
  isFilterValid = true;
  isMainFilterBlank = true;

  selectedFilterId: string | null = null;

  filterValueChanged: Subject<any>;

  typingDebounceTime: number | string;

  properties = {
    dataGridWidgetId: {
      type: String,
      value: null,
      observer: 'gridWidgetChange'
    },
    typingDebounceTime: {
      value: 400
    },
    isMainFilterBlank: {
      value: true,
      readOnly: true
    }
  };

  created(): void {
    log('created called');
    this.filterValueChanged = new Subject<void>();
  }

  ready(): void {
    log('ready called');
    super.ready();
    this.subscribeToUserChanges();
  }

  attached(): void {
    log('attached called');
    super.attached();
    this.attachWidgetListeners();
    this.updateIsFilterValid();
    this.updateIsMainFilterBlank();
  }

  attachWidgetListeners(): void {
    log('attachWidgetListeners called');
    let filterContent: HTMLElement | null = this.querySelector('.pds-grid-widget-filter-content');
    if (filterContent != null) {
      filterContent.addEventListener('input', (event: Event) => {
        log('input event from a grid-widget-filter');
        (event as any).skipDirtyStateHandling = true;
      });

      filterContent.addEventListener('change', (event: Event) => {
        log('change event from a grid-widget-filter');
        (event as any).skipDirtyStateHandling = true;
        setTimeout(() => {
          this.updateIsFilterValid();
        });
      });

      filterContent.addEventListener('renderedElementValueChange', () => {
        log('renderedElementValueChange event from a grid-widget-filter');
        this.filterValueChanged.next();
      });
    }
  }

  subscribeToUserChanges(): void {
    log('subscribeToUserChanges called');
    this.filterValueChanged.pipe(
      map((res: any) => {
        log('filterValue changed');
        //Every change we do needs to check to see if things are valid.
        this.updateIsFilterValid();
        this.updateIsMainFilterBlank();

        return res;
      }),
      debounceTime(Number(this.typingDebounceTime))
    ).subscribe((res: any) => {
      this.runTask();
    });
  }

  updateIsFilterValid(): void {
    log('updateIsFilterValid');
    let isValid = true;
    let elements = this.querySelectorAll('pds-date-widget, pds-number-widget, pds-text-widget');

    for (let element of elements) {
      isValid = isValid && element.isValid;
    }
    this.isFilterValid = isValid;
  }

  updateIsMainFilterBlank(): void {
    log('updateIsMainFilterBlank');
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (firstFilterContainer != null) {
      this.setPrivatePolymerPropertyValue('isMainFilterBlank', firstFilterContainer.filter.isBlank());
    }
  }

  //Called when the grid widget id changes.
  gridWidgetChange(): void {
    log('gridWidgetChange called');
    if (this.dataGridWidgetId != null) {
      this.subscriptions.push(serviceInstances.getGridWidgetService().getGridWidget(this.dataGridWidgetId).subscribe((gridWidget: GridWidget | undefined) => {
        this.gridWidget = gridWidget || null;
        if (this.gridWidget != null) {
          this.updateSelectedFilterId();
          this.watch('gridWidget.filterControl.filterContainers');
          this.watch('gridWidget.hideFilterControls');
          this.watch('gridWidget.filterConstraint');
          this.watch('gridWidget.taskChainComplete.result');
          if (this.gridWidget.paginationControl) {
            this.watch('gridWidget.paginationControl.numberOfRows'); //This is the one we need
          }
          this.subscriptions.push(this.gridWidget.initComplete.subscribe(() => {
            this.updateSelectedFilterId();
          }));
          this.subscriptions.push(this.gridWidget.tasksComplete.subscribe(() => {
            //This is run whenever the task chain completes.
            this.updateIsMainFilterBlank();
          }));
        }
      }));
    }
  }

  updateSelectedFilterId(): void {
    log('updateSelectFilterId called');
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (firstFilterContainer != null) {
      this.selectedFilterId = firstFilterContainer.dataField.id;
    }
  }

  showFilter(): boolean {
    log('showFilter called');
    return this.dataGridWidgetId != null && this.gridWidget != null && !this.gridWidget.hideFilterControls && this.gridWidget.filterConstraint !== 'no-filters';
  }

  enterKeyPressed(event): void {
    log('enterKeyPressed called');
    if (event.which === 13 &&
      (this.gridWidget != null && (this.gridWidget.filterConstraint === 'advanced-only' || this.gridWidget.filterConstraint == null))) {
      this.addAnotherFilter(event);
    }
  }

  /**
   * When the user asks for a another advanced filter, this function is called.
   * It creates a new GridWidgetFilter object and puts it into the array of
   * advance filter rows.
   *
   * @param event (optional)
   */
  addAnotherFilter(event): void {
    log('addAnotherFilter called');
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (!this.isFilterValid ||
      this.gridWidget == null ||
      firstFilterContainer == null ||
      firstFilterContainer.filter.isBlank()) {
      return;
    }

    this.gridWidget.filterControl.addAnotherFilter();
    //Since we added a new filter, we need to update the firstFilterContainer variable.
    firstFilterContainer = this.getFirstFilterContainer();
    if (firstFilterContainer != null) {
      this.selectedFilterId = firstFilterContainer.filter.dataField.id;
    }

    if (event) {
      setTimeout(function (): void {
        let fieldset = pdsUtils.getClosest(event.target, 'fieldset');
        if (fieldset != null) {
          fieldset.querySelector('div:last-of-type select:first-of-type').focus();
        }
      }, 0);
    }

    //When we add a filter, the next one should be blank so we need to update the variable.
    this.updateIsMainFilterBlank();
  }

  getFirstFilterContainer(): FilterContainer | undefined {
    if (this.gridWidget == null || this.gridWidget.filterControl.filterContainers.length === 0) {
      return;
    }

    return this.gridWidget.filterControl.filterContainers[0];
  }

  isAddFilterButtonDisabled(): boolean {
    log('isAddFilterButtonDisabled called');
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (firstFilterContainer != null) {
      return firstFilterContainer.filter.isBlank() || !this.isFilterValid;
    } else {
      return true;
    }
  }

  showActiveFilters(): boolean {
    return this.gridWidget != null && this.gridWidget.filterConstraint !== 'simple-only' &&
      this.gridWidget.filterControl.filterContainers.length > 1;
  }

  clearFilterControls(): void {
    log('clearFilterControls called');
    if (this.gridWidget == null) {
      return;
    }

    this.gridWidget.clearFilterControls();
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (firstFilterContainer != null) {
      this.selectedFilterId = firstFilterContainer.dataField.id;
      this.redrawTrigger = !this.redrawTrigger;
    }

    this.updateIsMainFilterBlank();
  }

  removeFilter(event: Event): void {
    log('removeFilter called');
    if (this.gridWidget == null) {
      return;
    }

    let index: number | undefined = this.extractProperty(event, 'button', 'index');
    if (index == null) {
      index = 0;
    }
    this.gridWidget.filterControl.removeFilter(index);
    this.runTask();
    this.switchFocus();
  }

  runTask(taskName?: string): void {
    log('pdsGridWidgetFilter.runTask is running.');
    if (this.gridWidget != null && pdsValidation.validateElements(this.querySelector('.pds-filter-controls'), false)) {
      log('The elements were valid.  Executing task chain');
      this.gridWidget.executeTaskChain(taskName);
    }
  }

  switchFocus(): void {
    log('switchFocus called');
    let availableFilterContainers = <HTMLElement>this.querySelector('.pds-selection-status');
    setTimeout(() => {
      if (availableFilterContainers != null) {
        let availableButtons = pdsUtils.getAllTabbableElements(availableFilterContainers);
        if (availableButtons != null) {
          let nextFocusableElement = pdsUtils.getTabbableElementAtIndex(availableButtons, availableButtons.length - 1);
          if (nextFocusableElement != null) {
            nextFocusableElement.focus();
          }
        }
      }
    });
  }

  getFilterResult(): string {
    if (this.gridWidget == null) {
      return '';
    }

    if (this.gridWidget.paginationControl != null) {
      return pdsI18n.getText(this.gridWidget.paginationControl.numberOfRows === 1 ? 'pds|grid widget|1 Result' : 'pds|grid widget|xx Results',  {number: this.gridWidget.paginationControl.numberOfRows});
    } else {
      if (this.gridWidget.taskChainComplete.result == null) {
        return '';
      }
      return pdsI18n.getText(this.gridWidget.taskChainComplete.result.length === 1 ? 'pds|grid widget|1 Result' : 'pds|grid widget|xx Results', {number: this.gridWidget.taskChainComplete.result.length});
    }
  }

  getSelectableFilterFields(): Array<{ value: string, text: string }> {
    log('getSelectableFilterFields called');
    if (this.gridWidget == null) {
      log('returning empty');
      return [];
    }

    return this.gridWidget.filterControl.getSelectableFilterFields().map((value: GridWidgetDataField) => {
      return {value: value.id, text: value.title};
    });
  }

  /**
   * This function is called when the user changes the filter type they want to filter on.
   * @param event  event.target.value is the new dataField id that is selected.
   */
  updateSelectedFilter(event: Event): void {
    log('updateSelectedFilter called');
    if (this.gridWidget == null) {
      return;
    }

    //This event comes from the select that selects the filter type.
    //The value is the DataField Id.
    let newSelectedFieldId = (event.target as any).value;
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (firstFilterContainer == null) {
      return;
    }
    this.setPrivatePolymerPropertyValue('isMainFilterBlank', firstFilterContainer.filter.isBlank());
    this.gridWidget.filterControl.filterFieldUpdated(firstFilterContainer, newSelectedFieldId);
    this.selectedFilterId = firstFilterContainer.filter.dataField.id;
    this.gridWidget.executeTaskChain();
  }

  getSelectedComponentConfig(): ElementConfiguration | null {
    let firstFilterContainer: FilterContainer | undefined = this.getFirstFilterContainer();
    if (firstFilterContainer == null) {
      return null;
    }

    return this.getComponentConfig(firstFilterContainer);
  }

  getComponentConfig(filterContainer: FilterContainer): ElementConfiguration | null {
    return {
      tagName: filterContainer.filter.getTagName(),
      properties: {
        filter: filterContainer.filter
      },
      attributes: {
        id: this.dataGridWidgetId + '-' + filterContainer.dataField.id
      },
      scopeClass: 'pds-grid-widget-filter'
    };
  }

  getTagText(filterContainer: FilterContainer): string {
    //You would think that we could call this from the template... but no.
    //But if we pass the object into this function, then it can call the method and return it.
    //Why does this work and not the other way?  I dunno. But it does and we like that.
    return filterContainer.filter.getTagText();
  }

  detached(): void {
    log('detached called');
    super.detached();
    this.cancelSubscriptions();
  }
}
